import React from "react";
import SeoHead from "../components/SeoHead";
import { localeRoutes } from "../i18n/siteContent";

const mecspeLinkClass =
    "font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 transition hover:text-amber-600 hover:decoration-amber-400";

const contentByLocale = {
    en: {
        title: "Event Platform Architecture Case Study | Maurizio Brioschi",
        description:
            "Architecture and integration behind the digital platform supporting MECSPE industrial fairs, including CRM, ticketing and exhibitor systems.",
        eyebrow: "Case Study",
        heading: "Scalable Digital Platform for Industrial Events",
        subtitle:
            "Architecture and system integration behind the digital platform used by MECSPE and other major industrial fairs.",
        contextTitle: "CONTEXT",
        context: [
            <>
                For about a year and a half I worked on the development of the digital platform behind{" "}
                <a
                    href="https://www.mecspe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={mecspeLinkClass}
                >
                    MECSPE
                </a>
                , one of the most important industrial industrial fairs in Italy.
            </>,
            "The platform is not just a website but a full digital ecosystem supporting exhibitors, visitors and event organizers."
        ],
        challengeTitle: "THE CHALLENGE",
        challengeIntro:
            "The main challenge was building a system capable of supporting a large-scale event ecosystem while integrating multiple enterprise systems and with only one admin panel to manage everything.",
        challenge: [
            "Integration of a CMS to update the content of the portal",
            "integration with Salesforce CRM for exhibitor and customer data",
            "ticketing and visitor acquisition via an external provider",
            "management of exhibition stands and technical layout plans via an external provider and ftp directory",
            "exhibitor self-service platform to manage their own profile",
            "real-time appointment and lead management during events",
            "coordination between different technical teams and stakeholders"
        ],
        challengeClosing:
            "The platform had to remain reliable even during peak traffic when thousands of users access the system simultaneously.",
        solutionTitle: "THE SOLUTION",
        solutionIntro:
            "The solution was a modular digital platform integrating multiple systems into a coherent operational ecosystem.",
        solution: [
            "CMS to manage the dynamic content of the portal",
            "CRM synchronization with Salesforce",
            "ticketing integration with Vivaticket as external provider",
            "exhibitor management platform with proprietary code",
            "management of exhibition stands and technical layout plans via FTP and external service",
            "PWA application used during events to manage meetings and contacts",
            "centralized data synchronization between systems"
        ],
        highlightsTitle: "ARCHITECTURE HIGHLIGHTS",
        highlights: [
            "enterprise integrations",
            "CMS integration",
            "CRM synchronization",
            "Proprietary event platform architecture",
            "Easy configurable platform for different events",
            "scalable system design",
            "operational reliability",
            "PWA live-event support",
            "Statistics about the events"
        ],
        resultsTitle: "RESULTS",
        resultsIntro: "The platform delivered measurable results:",
        stats: [
            { value: "64,753", label: "registrations in the first 3 months" },
            { value: "65,000+", label: "tickets generated" },
            { value: "5,000+", label: "daily visits during the event lifecycle" },
            { value: "Enterprise", label: "integrations across CRM, ticketing and exhibitor systems" }
        ],
        results: [
            "digital transformation, automating a lot of processes that were done manually before",
            "thousands of exhibitors and visitors supported",
            "The system successfully supported real-world events with thousands of concurrent users."
        ],
        roleTitle: "MY ROLE",
        roleIntro: "My role in the project included:",
        role: [
            "Team leader and internal project manager",
            "solution architecture design",
            "system integration strategy",
            "technical leadership",
            "development coordination",
            "ensuring platform reliability during live events",
            "APIs code development and supervisor"
        ],
        reflectionTitle: "FINAL REFLECTION",
        reflection:
            "Complex event ecosystems require more than isolated technical solutions. They require reliable architecture, strong integration strategy, and calm execution under operational pressure.",
        ctaTitle: "Need a platform that has to work under real pressure?",
        ctaBody:
            "I support companies on digital transformation, platform architecture, system integrations, fractional CTO engagements, and platform modernization.",
        cta: "Book a Strategy Call"
    },
    it: {
        title: "Case Study Architettura Piattaforma Eventi | Maurizio Brioschi",
        description:
            "Architettura e integrazioni della piattaforma digitale che supporta MECSPE e altri eventi fieristici, con CMS,  CRM, ticketing e sistemi per espositori.",
        eyebrow: "Case Study",
        heading: "Piattaforma digitale scalabile per eventi fiestistici",
        subtitle:
            "Architettura e integrazione sistemi dietro la piattaforma digitale usata da MECSPE e da altri importanti eventi fieristici.",
        contextTitle: "CONTESTO",
        context: [
            <>
                Per circa un anno e mezzo ho lavorato allo sviluppo della piattaforma digitale dietro{" "}
                <a
                    href="https://www.mecspe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={mecspeLinkClass}
                >
                    MECSPE
                </a>
                , una delle più importanti fiere industriali in Italia.
            </>,
            "La piattaforma non è un semplice sito ma un ecosistema digitale completo a supporto di espositori, visitatori e organizzatori."
        ],
        challengeTitle: "LA SFIDA",
        challengeIntro:
            "La sfida principale era costruire un sistema capace di supportare un ecosistema fieristico su larga scala, integrando più sistemi enterprise e un solo pannello di controllo per gestire tutto.",
        challenge: [
            "integrazione di un CMS per aggiornare i contenuti del portale",
            "integrazione con Salesforce CRM per dati espositori e clienti",
            "ticketing e acquisizione visitatori tramite un provider esterno",
            "gestione stand espositivi e layout tecnici tramite un provider esterno e directory FTP",
            "piattaforma self-service per gli espositori per gestire il proprio profilo",
            "gestione in tempo reale di appuntamenti e lead durante gli eventi",
            "coordinamento tra team tecnici differenti e stakeholder"
        ],
        challengeClosing:
            "La piattaforma doveva restare affidabile anche nei momenti di picco, quando migliaia di utenti accedono contemporaneamente al sistema.",
        solutionTitle: "LA SOLUZIONE",
        solutionIntro:
            "La soluzione è stata una piattaforma digitale modulare capace di integrare più sistemi in un ecosistema operativo coerente.",
        solution: [
            "CMS per gestire i contenuti dinamici del portale",
            "sincronizzazione CRM con Salesforce",
            "integrazione ticketing con Vivaticket come provider esterno",
            "piattaforma proprietaria per la gestione espositori",
            "gestione di stand espositivi e layout tecnici tramite FTP e servizio esterno",
            "applicazione PWA usata durante gli eventi per gestire incontri e contatti",
            "sincronizzazione dati centralizzata tra i sistemi"
        ],
        highlightsTitle: "ARCHITECTURE HIGHLIGHTS",
        highlights: [
            "integrazioni enterprise",
            "integrazione CMS",
            "sincronizzazione CRM",
            "architettura proprietaria della piattaforma evento",
            "piattaforma facilmente configurabile per eventi diversi",
            "design di sistema scalabile",
            "affidabilità operativa",
            "supporto PWA durante l'evento",
            "statistiche sugli eventi"
        ],
        resultsTitle: "RISULTATI",
        resultsIntro: "La piattaforma ha prodotto risultati misurabili:",
        stats: [
            { value: "64.753", label: "registrazioni nei primi 3 mesi" },
            { value: "65.000+", label: "ticket generati" },
            { value: "5.000+", label: "visite giornaliere nel ciclo di vita dell'evento" },
            { value: "Enterprise", label: "integrazioni tra CRM, ticketing e sistemi per espositori" }
        ],
        results: [
            "trasformazione digitale con automazione di molti processi prima gestiti manualmente",
            "migliaia di espositori e visitatori supportati",
            "Il sistema ha supportato eventi reali con migliaia di utenti simultanei."
        ],
        roleTitle: "IL MIO RUOLO",
        roleIntro: "Il mio contributo al progetto ha incluso:",
        role: [
            "team leader e project manager interno",
            "solution architecture design",
            "strategia di integrazione sistemi",
            "technical leadership",
            "coordinamento dello sviluppo",
            "garantire l'affidabilita della piattaforma durante gli eventi live",
            "sviluppo e supervisione del codice API"
        ],
        reflectionTitle: "RIFLESSIONE FINALE",
        reflection:
            "Ecosistemi evento complessi richiedono più di singole soluzioni tecniche. Richiedono architettura affidabile, una strategia di integrazione solida e una execution lucida sotto pressione operativa.",
        ctaTitle: "Hai bisogno di una piattaforma che deve reggere sotto pressione reale?",
        ctaBody:
            "Supporto aziende su digital transformation, platform architecture, system integrations, engagement come fractional CTO e platform modernization.",
        cta: "Prenota una Strategy Call"
    }
};

function EventPlatformArchitectureCaseStudyPage({ locale = "en" }) {
    const text = contentByLocale[locale] || contentByLocale.en;
    const routes = localeRoutes[locale];
    const breadcrumbItems = [
        { name: "Home", path: routes.home },
        { name: locale === "it" ? "Casi studio" : "Case Studies", path: routes.caseStudies },
        { name: text.heading, path: routes.eventPlatformCaseStudy }
    ];

    return (
        <>
            <SeoHead
                title={text.title}
                description={text.description}
                keywords="Maurizio Brioschi MECSPE case study, event platform architecture, solution architect, enterprise integrations, digital transformation, CRM integration, ticketing integration"
                path={routes.eventPlatformCaseStudy}
                locale={locale}
                enPath={localeRoutes.en.eventPlatformCaseStudy}
                itPath={localeRoutes.it.eventPlatformCaseStudy}
                breadcrumbItems={breadcrumbItems}
            />
            <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
                <header>
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">{text.eyebrow}</p>
                    <h1 className="mt-3 text-4xl font-impact text-slate-900">{text.heading}</h1>
                    <p className="mt-4 max-w-4xl text-lg text-slate-700">{text.subtitle}</p>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        {text.stats.map((item) => (
                            <article key={item.label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-3xl font-impact text-slate-900">{item.value}</p>
                                <p className="mt-2 text-sm text-slate-600">{item.label}</p>
                            </article>
                        ))}
                    </div>
                </header>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.contextTitle}</h2>
                    <div className="mt-4 space-y-4 text-sm text-slate-700">
                        {text.context.map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                        ))}
                    </div>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.challengeTitle}</h2>
                    <p className="mt-4 text-sm text-slate-700">{text.challengeIntro}</p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        {text.challenge.map((item) => (
                            <li key={item}>- {item}</li>
                        ))}
                    </ul>
                    <p className="mt-4 text-sm text-slate-700">{text.challengeClosing}</p>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.solutionTitle}</h2>
                    <p className="mt-4 text-sm text-slate-700">{text.solutionIntro}</p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        {text.solution.map((item) => (
                            <li key={item}>- {item}</li>
                        ))}
                    </ul>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.highlightsTitle}</h2>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {text.highlights.map((item) => (
                            <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
                                {item}
                            </div>
                        ))}
                    </div>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.resultsTitle}</h2>
                    <p className="mt-4 text-sm text-slate-700">{text.resultsIntro}</p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        {text.results.map((item) => (
                            <li key={item}>- {item}</li>
                        ))}
                    </ul>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.roleTitle}</h2>
                    <p className="mt-4 text-sm text-slate-700">{text.roleIntro}</p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        {text.role.map((item) => (
                            <li key={item}>- {item}</li>
                        ))}
                    </ul>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
                    <h2 className="text-2xl font-impact text-slate-900">{text.reflectionTitle}</h2>
                    <p className="mt-4 text-sm text-slate-700">{text.reflection}</p>
                </section>

                <section className="mt-8 rounded-2xl border border-slate-200 bg-slate-900 p-6 text-white">
                    <h2 className="text-2xl font-impact">{text.ctaTitle}</h2>
                    <p className="mt-4 max-w-3xl text-sm text-slate-100">{text.ctaBody}</p>
                    <a
                        href={routes.contact}
                        className="mt-5 inline-flex rounded-full bg-amber-500 px-5 py-3 text-sm font-semibold text-slate-900 transition hover:bg-amber-400"
                    >
                        {text.cta}
                    </a>
                </section>
            </main>
        </>
    );
}

export default EventPlatformArchitectureCaseStudyPage;
